import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle, ShieldOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useContract } from '@/hooks/api/use-contracts';
import { useSession } from '@/lib/auth-client';
import { usePermissions } from '@/hooks/api/use-permissions';
import { LawyerReviewPanel } from '@/components/review/lawyer-review-panel';
import { CreatorChangesView } from '@/components/review/creator-changes-view';

export const Route = createFileRoute('/_authenticated/contracts/$contractId/review')({
  component: ReviewPage,
});

function ReviewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { contractId } = Route.useParams();

  const { data: contract, isPending, isError } = useContract(contractId);
  const { data: permissions } = usePermissions();
  const { data: session } = useSession();

  const currentUserRole = permissions?.role ?? '';
  const currentUserId = session?.user?.id ?? '';
  const workspaceSuspended = permissions?.workspaceStatus === 'SUSPENDED';

  // Loading state
  if (isPending) {
    return (
      <div className="flex items-center justify-center h-full py-24">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error state
  if (isError || !contract) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 h-full py-24 text-center">
        <AlertCircle className="size-12 text-destructive" />
        <p className="text-sm text-muted-foreground">{t('contractDetail.loadError')}</p>
        <Button
          variant="outline"
          onClick={() => navigate({ to: '/contracts', search: { status: undefined } })}
          className="cursor-pointer"
        >
          {t('contractDetail.backToList')}
        </Button>
      </div>
    );
  }

  // Guard: only DRAFT, IN_REVIEW, CHANGES_REQUESTED, or PENDING_APPROVAL state
  if (
    contract.status !== 'DRAFT' &&
    contract.status !== 'IN_REVIEW' &&
    contract.status !== 'CHANGES_REQUESTED' &&
    contract.status !== 'PENDING_APPROVAL'
  ) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 h-full py-24 text-center">
        <ShieldOff className="size-12 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">{t('review.guard.wrongState')}</p>
        <Button
          variant="outline"
          onClick={() => navigate({ to: '/contracts/$contractId', params: { contractId } })}
          className="cursor-pointer"
        >
          {t('review.guard.backToContract')}
        </Button>
      </div>
    );
  }

  const isCreator = contract.createdBy === currentUserId;

  // DRAFT + Creator → CreatorChangesView (editable, no lawyer context)
  if (contract.status === 'DRAFT' && isCreator) {
    return (
      <div className="fixed inset-0 z-40 lg:left-56 bg-background">
        <CreatorChangesView contract={contract} workspaceSuspended={workspaceSuspended} />
      </div>
    );
  }

  // DRAFT + non-creator → redirect back (only creator can edit draft)
  if (contract.status === 'DRAFT') {
    return (
      <div className="flex flex-col items-center justify-center gap-4 h-full py-24 text-center">
        <ShieldOff className="size-12 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">{t('review.guard.notCreator')}</p>
        <Button
          variant="outline"
          onClick={() => navigate({ to: '/contracts/$contractId', params: { contractId } })}
          className="cursor-pointer"
        >
          {t('review.guard.backToContract')}
        </Button>
      </div>
    );
  }

  const isAssignedLawyer = contract.assignedLawyerId === currentUserId;
  const isAdminOrOwner = currentUserRole === 'ADMIN' || currentUserRole === 'OWNER';
  // Viewer context for banners/footers: creator sees their own messages,
  // observer (admin/owner on someone else's contract) sees neutral status messages
  const viewerContext: 'creator' | 'lawyer' | 'observer' = isCreator
    ? 'creator'
    : isAssignedLawyer
      ? 'lawyer'
      : 'observer';

  // PENDING_APPROVAL → read-only view for both creator and lawyer
  if (contract.status === 'PENDING_APPROVAL') {
    return (
      <div className="fixed inset-0 z-40 lg:left-56 bg-background">
        <CreatorChangesView
          contract={contract}
          workspaceSuspended={workspaceSuspended}
          readOnly={!isCreator}
          viewerContext={viewerContext}
        />
      </div>
    );
  }

  // CHANGES_REQUESTED + Creator → CreatorChangesView (editable)
  if (contract.status === 'CHANGES_REQUESTED' && isCreator) {
    return (
      <div className="fixed inset-0 z-40 lg:left-56 bg-background">
        <CreatorChangesView
          contract={contract}
          workspaceSuspended={workspaceSuspended}
          viewerContext="creator"
        />
      </div>
    );
  }

  // CHANGES_REQUESTED + Lawyer/Admin/Owner → CreatorChangesView (read-only)
  if (contract.status === 'CHANGES_REQUESTED') {
    return (
      <div className="fixed inset-0 z-40 lg:left-56 bg-background">
        <CreatorChangesView
          contract={contract}
          workspaceSuspended={workspaceSuspended}
          readOnly
          viewerContext={viewerContext}
        />
      </div>
    );
  }

  // IN_REVIEW + Creator → read-only view of their submitted changes
  if (contract.status === 'IN_REVIEW' && isCreator) {
    return (
      <div className="fixed inset-0 z-40 lg:left-56 bg-background">
        <CreatorChangesView
          contract={contract}
          workspaceSuspended={workspaceSuspended}
          readOnly
          viewerContext="creator"
        />
      </div>
    );
  }

  // IN_REVIEW → assigned lawyer gets full review panel
  if (isAssignedLawyer) {
    return (
      <div className="fixed inset-0 z-40 lg:left-56 bg-background">
        <LawyerReviewPanel contract={contract} workspaceSuspended={workspaceSuspended} />
      </div>
    );
  }

  // OWNER / ADMIN can view but not act — read-only with observer context
  if (isAdminOrOwner) {
    return (
      <div className="fixed inset-0 z-40 lg:left-56 bg-background">
        <CreatorChangesView
          contract={contract}
          workspaceSuspended={workspaceSuspended}
          readOnly
          viewerContext="observer"
        />
      </div>
    );
  }

  // MEMBER or other — no access
  return (
    <div className="flex flex-col items-center justify-center gap-4 h-full py-24 text-center">
      <ShieldOff className="size-12 text-muted-foreground" />
      <p className="text-sm text-muted-foreground">{t('review.guard.notLawyer')}</p>
      <Button
        variant="outline"
        onClick={() => navigate({ to: '/contracts/$contractId', params: { contractId } })}
        className="cursor-pointer"
      >
        {t('review.guard.backToContract')}
      </Button>
    </div>
  );
}
